"use client"

import * as React from "react"
import { Household } from "@/schema"
import { ComboboxInput } from "@ui/forms/inputs/combobox-input"
import { Button } from "@ui/shad"
import { cn } from "@ui/utils"
import { ChevronsUpDown } from "lucide-react"

export type Option<T = Household> = {
  value: string
  label: string
  icon?: React.ReactNode
  data?: T | null
}

export interface BadgeProps<T = Household> {
  id: string
  value: string
  onValueChange: (value: string | number) => void
  disabled?: boolean
  options: Option<T>[]
  onSearch?: (query: string) => void | Promise<void>
  isSearching?: boolean
  children?: React.ReactNode
  className?: string
  placeholder?: string
  triggerVariant?: "badge" | "field"
}

export function BadgeComboboxSelect<T = Household>({
  id,
  children,
  options,
  disabled = false,
  value,
  onValueChange,
  onSearch,
  isSearching = false,
  className,
  placeholder = "Select an option",
  triggerVariant = "badge",
}: BadgeProps<T>) {
  const hasValue = value !== ""
  const isFieldTrigger = triggerVariant === "field"

  return (
    <ComboboxInput
      id={id}
      options={options}
      value={value}
      onChange={(value) => onValueChange(value as string | number)}
      onSearch={onSearch}
      isSearching={isSearching}
      placeholder={placeholder}
      className={cn(isFieldTrigger && "w-full")}
      contentProps={
        isFieldTrigger
          ? { align: "start", className: "w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] p-0" }
          : undefined
      }
      renderOption={(option) => (
        <div className="flex h-full w-full cursor-pointer items-center gap-2">
          {option.icon}
          <span>{option.label}</span>
        </div>
      )}
      triggerButton={
        <Button
          variant="outline"
          className={cn(
            isFieldTrigger
              ? "h-10 w-full max-w-none justify-between px-3 text-left font-normal"
              : `gap-4 ${hasValue ? "default" : "border-dashed"} flex h-fit w-fit max-w-72 items-center justify-end px-2`,
            className
          )}
          size={isFieldTrigger ? "default" : "lg"}
          disabled={disabled}
          type="button"
        >
          {children}
          {isFieldTrigger && <ChevronsUpDown className="text-muted-foreground shrink-0 opacity-50" />}
        </Button>
      }
    />
  )
}
